import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

/**
 * One time-series for a sensor reading. {@code points} are sorted by date asc; the chart bins
 * them on the x axis. Caller controls the unit / colour via {@code label} and {@code colour}.
 */
export interface TelemetrySeries {
  /** Display label (e.g. "Battery SOC", "Pressure"). */
  label: string;
  /** Tailwind colour token used to stroke the curve, e.g. "primary-500" or "eletricPurple-500". */
  colour: string;
  unit?: string;
  points: Array<{ ts: string | number; value: number }>;
}

export type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

/**
 * V7 integrator telemetry view. Renamed from "graphiques" — the term was non-evocative for
 * client end-users, "Telemetry" matches the IoT vocabulary.
 *
 * <p>Implementation note: this component is intentionally a placeholder shell — once the
 * design is final, plug a chart lib (apexcharts is already in {@code package.json}). For now
 * we render the latest value of each series and the time-range selector so the page wiring
 * works end-to-end. Replace the content of the {@code .chart-canvas} block with the chart
 * widget once the visuals are decided.
 */
@Component({
  selector: 'lib-telemetry-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <header class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-gray-700">Telemetry</h3>
        <div class="flex gap-1">
          @for (range of ranges; track range) {
            <button
              type="button"
              class="rounded px-2 py-1 text-xs"
              [class.bg-primary-500]="selectedRange() === range"
              [class.text-white]="selectedRange() === range"
              [class.text-gray-600]="selectedRange() !== range"
              (click)="onRangeChange(range)">
              {{ range }}
            </button>
          }
        </div>
      </header>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        @for (s of series(); track s.label) {
          <div class="rounded border border-gray-200 bg-gray-50 p-3">
            <div class="text-xs uppercase tracking-wide text-gray-500">{{ s.label }}</div>
            <div class="mt-1 font-mono text-2xl" [class]="'text-' + s.colour">
              {{ formatValue(latest(s)) }}<span class="ml-1 text-xs text-gray-500">{{ s.unit ?? '' }}</span>
            </div>
            <div class="mt-1 text-xs text-gray-400">{{ s.points.length }} points</div>
          </div>
        } @empty {
          <p class="col-span-full text-sm text-gray-500">No telemetry data for this range.</p>
        }
      </div>

      <!--
        Chart canvas placeholder. Bind apexcharts (already in package.json) here once the design
        is locked. Series colours come from {@code series().colour} (Wheere palette tokens).
      -->
      <div
        class="chart-canvas h-48 rounded border border-dashed border-gray-300 bg-gray-50 p-2 text-center text-xs text-gray-400">
        Chart canvas — wire apexcharts here
      </div>
    </div>
  `,
})
export class TelemetryChartComponent {
  series = input.required<TelemetrySeries[]>();

  /** Defaults to last 24h — common for IoT monitoring. */
  range = input<TimeRange>('24h');

  protected readonly ranges: TimeRange[] = ['1h', '6h', '24h', '7d', '30d'];
  protected readonly selectedRange = signal<TimeRange>('24h');

  protected readonly _syncRange = computed(() => {
    // Keep input → signal in sync; the signal is what the template binds to so the user can
    // tap a range without notifying the parent.
    this.selectedRange.set(this.range());
    return this.range();
  });

  protected onRangeChange(r: TimeRange): void {
    this.selectedRange.set(r);
  }

  protected latest(s: TelemetrySeries): number | null {
    if (!s.points.length) return null;
    return s.points[s.points.length - 1]?.value ?? null;
  }

  protected formatValue(v: number | null): string {
    if (v == null) return '—';
    return v.toFixed(1);
  }
}
